import { useState } from "react";
import type { ChangeEvent, MouseEvent } from "react";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControlLabel from "@mui/material/FormControlLabel";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import type { BackgroundJobKind } from "@/api/background-job/_models/BackgroundJob";
import { BACKGROUND_JOB_SCHEDULE_INTERVALS } from "@/api/background-job/_models/BackgroundJobSchedule";
import type { BackgroundJobScheduleFrequency, BackgroundJobScheduleInput } from "@/api/background-job/_models/BackgroundJobSchedule";
import { toJobScheduleDescription } from "@/features/job/job.utils";
import { JOB_SCHEDULE_WEEKDAYS } from "./jobSchedule.utils";

interface JobScheduleDialogProps {
  /** The schedule being edited, or a new one's defaults. */
  initial: BackgroundJobScheduleInput;
  isNew: boolean;
  isSaving: boolean;
  error: string | null;
  kinds: BackgroundJobKind[];
  onCancel: () => void;
  onSave: (schedule: BackgroundJobScheduleInput) => void;
}

/** Add or edit a schedule: its name, the job (schedulable kinds only), when it runs (Sydney time) and whether it's on. The page saves it. */
export const JobScheduleDialog = (props: JobScheduleDialogProps) => {
  const { initial, isNew, isSaving, error, kinds, onCancel, onSave } = props;
  const [schedule, setSchedule] = useState<BackgroundJobScheduleInput>(initial);
  const schedulableKinds = kinds.filter((kind) => kind.schedulable);
  const canSave = schedule.name.trim() !== "" && schedule.kind !== "" && !isSaving;

  const handleNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSchedule({ ...schedule, name: event.target.value });
  };

  const handleKindChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSchedule({ ...schedule, kind: event.target.value });
  };

  const handleFrequencyChange = (_event: MouseEvent<HTMLElement>, frequency: BackgroundJobScheduleFrequency | null) => {
    if (frequency) setSchedule({ ...schedule, frequency });
  };

  const handleIntervalChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSchedule({ ...schedule, intervalMinutes: Number(event.target.value) });
  };

  const handleMinuteChange = (event: ChangeEvent<HTMLInputElement>) => {
    const minute = Math.min(59, Math.max(0, Number(event.target.value) || 0));
    setSchedule({ ...schedule, minute });
  };

  const handleTimeChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSchedule({ ...schedule, time: event.target.value });
  };

  const handleWeekdaysChange = (_event: MouseEvent<HTMLElement>, weekdays: number[]) => {
    setSchedule({ ...schedule, weekdays });
  };

  const handleEnabledChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSchedule({ ...schedule, enabled: event.target.checked });
  };

  const handleSave = () => {
    onSave({ ...schedule, name: schedule.name.trim() });
  };

  return (
    <Dialog
      fullWidth
      maxWidth="xs"
      onClose={onCancel}
      open
    >
      <DialogTitle>{isNew ? "Add schedule" : "Edit schedule"}</DialogTitle>
      <DialogContent>
        <Stack
          spacing={2.5}
          sx={{ pt: 1 }}
        >
          {error && <Alert severity="error">{error}</Alert>}
          <TextField
            autoFocus
            label="Name"
            onChange={handleNameChange}
            placeholder="e.g. eStore stock pull"
            size="small"
            value={schedule.name}
          />
          <TextField
            label="Job"
            onChange={handleKindChange}
            select
            size="small"
            value={schedule.kind}
          >
            {schedulableKinds.map((kind) => (
              <MenuItem
                key={kind.kind}
                value={kind.kind}
              >
                {kind.label}
              </MenuItem>
            ))}
          </TextField>
          <ToggleButtonGroup
            exclusive
            fullWidth
            onChange={handleFrequencyChange}
            size="small"
            value={schedule.frequency}
          >
            <ToggleButton value="MINUTES">Every N min</ToggleButton>
            <ToggleButton value="HOURLY">Hourly</ToggleButton>
            <ToggleButton value="DAILY">Daily</ToggleButton>
          </ToggleButtonGroup>
          {schedule.frequency === "MINUTES" && (
            <TextField
              label="Every"
              onChange={handleIntervalChange}
              select
              size="small"
              value={schedule.intervalMinutes ?? 15}
            >
              {BACKGROUND_JOB_SCHEDULE_INTERVALS.map((minutes) => (
                <MenuItem
                  key={minutes}
                  value={minutes}
                >
                  {minutes} minutes
                </MenuItem>
              ))}
            </TextField>
          )}
          {schedule.frequency === "HOURLY" && (
            <TextField
              label="At minute"
              onChange={handleMinuteChange}
              size="small"
              type="number"
              value={schedule.minute ?? 0}
              slotProps={{ htmlInput: { min: 0, max: 59 } }}
            />
          )}
          {schedule.frequency === "DAILY" && (
            <>
              <TextField
                label="At (Sydney time)"
                onChange={handleTimeChange}
                size="small"
                type="time"
                value={schedule.time ?? "06:00"}
              />
              <Stack spacing={0.5}>
                <Typography
                  color="text.secondary"
                  variant="caption"
                >
                  On (none = every day)
                </Typography>
                <ToggleButtonGroup
                  fullWidth
                  onChange={handleWeekdaysChange}
                  size="small"
                  value={schedule.weekdays}
                >
                  {JOB_SCHEDULE_WEEKDAYS.map((weekday) => (
                    <ToggleButton
                      key={weekday.day}
                      value={weekday.day}
                    >
                      {weekday.label}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Stack>
            </>
          )}
          <Typography
            color="text.secondary"
            variant="body2"
          >
            Runs: {toJobScheduleDescription(schedule)} (Sydney time)
          </Typography>
          <FormControlLabel
            control={(
              <Switch
                checked={schedule.enabled}
                onChange={handleEnabledChange}
              />
            )}
            label={schedule.enabled ? "On" : "Off"}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <Button
          disabled={!canSave}
          onClick={handleSave}
          variant="contained"
        >
          {isSaving ? "Saving…" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
